// Fallback SMS automático para Bloquear/Desbloquear (pedido 2026-09-06) —
// reaproveitado pelos 3 pontos reais de envio desse comando:
// VehicleSendCommandDialog, VehicleDetailSidebar (routes/index.tsx) e
// VehicleDetails (mobile-app.tsx). Sem confirmação/polling no canal SMS
// (mesma limitação de sms-send.service.ts — a Veye não expõe status de
// entrega).
import { toast } from "sonner";
import { commandsService } from "@/services/commands.service";
import { smsSendService } from "@/services/sms-send.service";
import { reportsService } from "@/services/reports.service";
import { logActivity } from "@/services/gpsdrive-activity-log.service";
import { ApiError, apiGet } from "@/lib/api/client";
import { clearPendingBlockCommand, setPendingBlockCommand } from "@/lib/pending-block-commands";
import { confirmBlockedFromCommand } from "@/services/vehicles.service";

// Modelos de rastreador (vehicle_metadata.trackerModel) que NÃO bloqueiam
// por GPRS — bloquear/desbloquear vai sempre pelo SMS do catálogo de
// comandos, e a tela fica aguardando até o rastreador reportar o bloqueio
// (pedido 2026-09-24: "no J4 sempre usa SMS, o J4 não bloqueia por GPRS").
// Valor = protocolo usado pra achar o comando SMS quando a ficha do
// veículo não tem protocolo preenchido (o J4 conecta no Traccar como
// easytrack; vários J4 cadastrados em massa ficam sem protocolo na ficha).
const SMS_ONLY_TRACKER_MODELS: Record<string, string> = { J4: "easytrack" };

const normalizeModel = (model: string | null | undefined) => (model ?? "").trim().toUpperCase();

export function isSmsOnlyTrackerModel(model: string | null | undefined): boolean {
  return normalizeModel(model) in SMS_ONLY_TRACKER_MODELS;
}

export function defaultProtocolForTrackerModel(model: string | null | undefined): string | null {
  return SMS_ONLY_TRACKER_MODELS[normalizeModel(model)] ?? null;
}

// Quanto esperar o rastreador CONFIRMAR um bloqueio/desbloqueio enviado por
// GPRS antes de mandar o SMS (pedido 2026-09-24: J4 sem alimentação,
// "online" pro Traccar mas dormindo — o Traccar entregou o comando na
// conexão, respondeu 200, e o SMS nunca foi tentado).
const GPRS_CONFIRM_WAIT_MS = 45_000;
// A resposta do rastreador costuma chegar em 2–4 s (log do Traccar
// 2026-10-15) — confere rápido pra liberar a tela logo.
const GPRS_CONFIRM_POLL_MS = 2_000;
// SMS de fallback demora mais (operadora + rastreador acordar): espera a
// confirmação por mais tempo antes de liberar o "aguardando".
const SMS_FALLBACK_WAIT_MS = 10 * 60_000;
// Um watcher por veículo — clicar de novo reinicia a espera.
const watchers = new Map<string, { cancelled: boolean }>();

// Resposta de erro do rastreador ("...Fail", "Error", "Invalid") — o resto
// ("RELAY,1#=Success!", "Cut off the fuel supply: Success!") é sucesso.
const FAILED_RESULT = /fail|error|invalid|wrong|denied/i;

type TrackerReply = { ok: true } | { ok: false; result: string } | null;

async function trackerConfirmed(vehicleId: string, action: "block" | "unblock", sinceIso: string): Promise<TrackerReply> {
  const [events, positions] = await Promise.all([
    reportsService.commandEvents([vehicleId], sinceIso, new Date().toISOString()).catch(() => []),
    apiGet<Array<{ serverTime?: string; attributes?: { blocked?: boolean } }>>(
      `/api/positions?deviceId=${vehicleId}`,
    ).catch(() => []),
  ]);
  const reply = events.find((e) => e.type === "commandResult" && e.eventTime >= sinceIso);
  if (reply) {
    const result = String(reply.result ?? "");
    return result && FAILED_RESULT.test(result) && !/success/i.test(result) ? { ok: false, result } : { ok: true };
  }
  const last = positions[0];
  return !!last?.serverTime &&
    last.serverTime >= sinceIso &&
    typeof last.attributes?.blocked === "boolean" &&
    last.attributes.blocked === (action === "block")
    ? { ok: true }
    : null;
}

// Roda em segundo plano (não trava quem chamou, sobrevive ao popup fechar):
// resposta do rastreador → libera o "aguardando" e já mostra o novo estado;
// sem resposta na janela → manda o SMS de fallback (se houver).
function watchGprsThenSms(vehicleId: string, action: "block" | "unblock", gt06CommandId: string | null) {
  const prev = watchers.get(vehicleId);
  if (prev) prev.cancelled = true;
  const me = { cancelled: false };
  watchers.set(vehicleId, me);
  const sinceIso = new Date(Date.now() - 2_000).toISOString();
  const deadline = Date.now() + GPRS_CONFIRM_WAIT_MS;

  void (async () => {
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, GPRS_CONFIRM_POLL_MS));
      if (me.cancelled) return;
      const reply = await trackerConfirmed(vehicleId, action, sinceIso);
      if (me.cancelled) return;
      if (reply) {
        watchers.delete(vehicleId);
        clearPendingBlockCommand(vehicleId);
        if (reply.ok) {
          confirmBlockedFromCommand(vehicleId, action === "block");
          toast.success(action === "block" ? "Veículo bloqueado" : "Veículo desbloqueado", {
            description: "O rastreador confirmou o comando.",
          });
        } else {
          toast.error(`O rastreador recusou o ${action === "block" ? "bloqueio" : "desbloqueio"}`, {
            description: reply.result,
          });
        }
        return;
      }
    }
    if (me.cancelled) return;
    watchers.delete(vehicleId);
    if (!gt06CommandId) return;
    const label = action === "block" ? "bloqueio" : "desbloqueio";
    try {
      await smsSendService.send({ deviceId: Number(vehicleId), gt06CommandId });
      setPendingBlockCommand(vehicleId, action, "sms", SMS_FALLBACK_WAIT_MS);
      toast.success(`Comando de ${label} reenviado por SMS`, {
        description: "O rastreador não confirmou pelo GPRS em 45s.",
      });
      logActivity({
        type: action === "block" ? "VEHICLE_BLOCK" : "VEHICLE_UNBLOCK",
        message: `Sem confirmação do ${label} por GPRS — reenviado por SMS`,
        metadata: { vehicleId, commandType: action, channel: "sms", reason: "gprs_no_confirmation" },
      });
    } catch (err) {
      toast.error(`O rastreador não confirmou o ${label} e o SMS falhou`, {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  })();
}

export interface SmsFallbackContext {
  smsEnabled: boolean;
  // Equipamento só-SMS (ver SMS_ONLY_TRACKER_MODELS) — nunca tenta GPRS.
  smsOnly: boolean;
  blockGt06CommandId: string | null;
  unblockGt06CommandId: string | null;
}

export type BlockCommandOutcome =
  | { channel: "gprs" }
  | { channel: "sms" }
  | { channel: "none"; reason: "offline" | "gprs_error" | "sms_unavailable"; error?: unknown };

// `offline` decide se tenta GPRS: um veículo offline não passa pelo
// Traccar de propósito (ele só enfileiraria o comando sem avisar que nada
// aconteceu de verdade ainda, ver comentário original em
// routes/index.tsx#sendBlockCommand) — vai direto pro fallback SMS, se
// configurado.
export async function sendBlockCommandWithSmsFallback(params: {
  vehicleId: string;
  action: "block" | "unblock";
  offline: boolean;
  fallback: SmsFallbackContext;
}): Promise<BlockCommandOutcome> {
  const { vehicleId, action, offline, fallback } = params;
  const gt06CommandId = action === "block" ? fallback.blockGt06CommandId : fallback.unblockGt06CommandId;
  const canFallback = fallback.smsEnabled && !!gt06CommandId;

  if (fallback.smsOnly) {
    if (!canFallback) {
      return {
        channel: "none",
        reason: "sms_unavailable",
        error: new Error(
          fallback.smsEnabled
            ? `Este equipamento só ${action === "block" ? "bloqueia" : "desbloqueia"} por SMS — cadastre o comando SMS de ${action === "block" ? "bloqueio" : "desbloqueio"} para o protocolo dele em Comandos.`
            : "Este equipamento só bloqueia por SMS e o envio de SMS não está ativo na conta.",
        ),
      };
    }
    // J4 nunca informa o bloqueio na posição — o backend grava o estado no
    // device assim que a operadora aceita o SMS, e é isso que a tela lê
    // (vehicles.service.ts#smsBlockedState). Se a gravação falhar, fica
    // "aguardando" até alguém reenviar.
    const res = await smsSendService.send({
      deviceId: Number(vehicleId),
      gt06CommandId: gt06CommandId as string,
      recordBlockState: true,
    });
    setPendingBlockCommand(vehicleId, action, "sms", res.blockStateRecorded ? 60_000 : null);
    return { channel: "sms" };
  }

  if (!offline) {
    try {
      await commandsService.send(vehicleId, action);
      // 200 do Traccar = entregue na conexão, NÃO executado. Rastreador
      // "online" mas dormindo (sem alimentação) nunca confirma — então
      // fica de olho e manda o SMS se a confirmação não vier.
      setPendingBlockCommand(vehicleId, action, "gprs", GPRS_CONFIRM_WAIT_MS);
      watchGprsThenSms(vehicleId, action, canFallback ? (gt06CommandId as string) : null);
      return { channel: "gprs" };
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      if (!canFallback) return { channel: "none", reason: "gprs_error", error: err };
      await smsSendService.send({ deviceId: Number(vehicleId), gt06CommandId: gt06CommandId as string });
      setPendingBlockCommand(vehicleId, action, "sms", SMS_FALLBACK_WAIT_MS);
      return { channel: "sms" };
    }
  }

  if (!canFallback) return { channel: "none", reason: "offline" };
  await smsSendService.send({ deviceId: Number(vehicleId), gt06CommandId: gt06CommandId as string });
  setPendingBlockCommand(vehicleId, action, "sms", SMS_FALLBACK_WAIT_MS);
  return { channel: "sms" };
}
